import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/storefront/Header';
import Footer from '@/components/storefront/Footer';
import ProductCard from '@/components/storefront/ProductCard';
import { Truck, ShieldCheck, CheckCircle, Headphones, RotateCcw, PackageCheck } from 'lucide-react';

const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } } };

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { formatPrice } = useCurrency();
  const { config } = useStoreConfig();

  useEffect(() => {
    api.get('/products?limit=12&sort=-created_at').then(r => {
      const withImages = (r.data.products || []).filter(p => p.images?.length > 0);
      setProducts(withImages);
    }).catch(() => {});
    api.get('/products/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const storeName = config?.store_name || 'Florashop';
  const firstProductImage = products[0]?.images?.[0]?.url || '';
  const heroImg = firstProductImage || (config?.media_url_base ? `${config.media_url_base}/brand/florashop-hero.jpg` : '');
  const productByCategory = categories.reduce((acc, cat) => {
    acc[cat] = products.find(p => p.category === cat || p.product_type === cat);
    return acc;
  }, {});

  const trustBadges = [
    { icon: Truck, title: 'Free Shipping', desc: 'Free on all orders', bg: '#16a34a' },
    { icon: RotateCcw, title: 'Easy Returns', desc: '30-day return window', bg: '#3b82f6' },
    { icon: ShieldCheck, title: 'Secure Checkout', desc: 'Stripe encrypted payments', bg: '#8b5cf6' },
    { icon: PackageCheck, title: 'Made to Order', desc: 'Carefully packed products', bg: '#7C8B6F' },
  ];

  return (
    <div data-testid="storefront-homepage" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Helmet>
        <title>{`${storeName} — ${config?.tagline || 'Premium Lifestyle Products'}`}</title>
        <meta name="description" content={config?.meta_description || config?.tagline || ''} />
        <meta property="og:title" content={storeName} />
        <meta property="og:description" content={config?.meta_description || ''} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section data-testid="hero-section" className="relative" style={{ minHeight: '85vh' }}>
        <div className="absolute inset-0">
          {heroImg ? (
            <img src={heroImg} alt={`${storeName} featured product`} className="w-full h-full object-cover" loading="eager" />
          ) : (
            <div className="w-full h-full" style={{ backgroundColor: '#7C8B6F' }} />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.3) 100%)' }} />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6" style={{ minHeight: '85vh' }}>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ color: '#fff' }}>
            {storeName}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="font-sans text-lg tracking-wide mb-8 max-w-2xl" style={{ color: 'rgba(255,255,255,0.86)' }}>
            {config?.tagline || '3D-printed desk organizers, vases, and sculptural home accents.'}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
            <Link to="/shop" data-testid="hero-cta-button"
              className="inline-block font-sans text-xs font-bold uppercase tracking-[0.15em] px-10 py-4 transition-all duration-300"
              style={{ backgroundColor: '#000', color: '#fff' }}
              onMouseEnter={e => { e.target.style.backgroundColor = '#E8545A'; }}
              onMouseLeave={e => { e.target.style.backgroundColor = '#000'; }}>
              Shop Now
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Category Blocks */}
      {categories.length > 0 && (
        <section className="py-16 md:py-20 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.slice(0, 3).map((cat, i) => (
              <motion.div key={cat} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
                <Link to={`/shop?category=${encodeURIComponent(cat)}`}
                  className="relative block aspect-[4/5] overflow-hidden group" style={{ backgroundColor: '#f0ece6' }}>
                  <img src={productByCategory[cat]?.images?.[0]?.url || products[i]?.images?.[0]?.url || heroImg}
                    alt={cat} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 60%)' }} />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-sans text-lg font-bold uppercase tracking-wider mb-2" style={{ color: '#fff' }}>{cat}</h3>
                    <span className="font-sans text-xs uppercase tracking-[0.15em] border-b pb-0.5 transition-colors"
                      style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>Discover</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="py-16 md:py-20 px-6 md:px-12 max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn} className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight" style={{ color: '#000' }}>Featured Designs</h2>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {products.slice(0, 8).map((p, i) => (
            <motion.div key={p.handle} initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={{ ...fadeIn, visible: { ...fadeIn.visible, transition: { ...fadeIn.visible.transition, delay: i * 0.08 } } }}>
              <ProductCard product={p} formatPrice={formatPrice} />
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/shop" className="font-sans text-sm font-medium transition-colors border-b pb-0.5"
            style={{ color: '#000', borderColor: '#000' }}>
            See all products &rarr;
          </Link>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#f5f2ed' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
            <img src={products[1]?.images?.[0]?.url || heroImg} alt={`${storeName} products`}
              className="w-full aspect-[4/5] object-cover" />
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
            <p className="font-sans text-xs uppercase tracking-[0.2em] mb-4" style={{ color: '#999' }}>Our Story</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6" style={{ color: '#000' }}>
              {config?.tagline || 'Functional decor for organized spaces'}
            </h2>
            <p className="font-sans text-sm leading-relaxed mb-4" style={{ color: '#555' }}>
              {config?.description || 'Florashop creates practical desk and home pieces with sculptural forms, lightweight materials, and everyday usefulness.'}
            </p>
            <p className="font-sans text-sm leading-relaxed mb-8" style={{ color: '#555' }}>
              Every listing is written to match the product shown, with clear pricing, product options, and policy pages available before checkout.
            </p>
            <Link to="/shop" className="inline-block font-sans text-xs font-bold uppercase tracking-[0.15em] px-8 py-3.5"
              style={{ backgroundColor: '#000', color: '#fff' }}>
              Shop Now
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 md:py-20 px-6 text-center" style={{ backgroundColor: '#FDFBF7' }}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn} className="max-w-lg mx-auto">
          <h2 className="font-serif text-3xl tracking-tight mb-3" style={{ color: '#000' }}>Don't Miss Anything!</h2>
          <p className="font-sans text-sm mb-6" style={{ color: '#666' }}>Subscribe for product updates and new design releases.</p>
          <div className="flex max-w-sm mx-auto">
            <input type="email" placeholder="Enter your email"
              className="flex-1 h-11 px-4 font-sans text-sm border border-r-0 outline-none focus:border-[#E8545A]"
              style={{ borderColor: 'rgba(0,0,0,0.12)', backgroundColor: '#fff' }} />
            <button className="h-11 px-6 font-sans text-xs font-bold uppercase tracking-wider"
              style={{ backgroundColor: '#000', color: '#fff' }}>Subscribe</button>
          </div>
        </motion.div>
      </section>

      {/* Trust Bar */}
      <section className="py-14 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)', backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {trustBadges.map(({ icon: Icon, title, desc, bg }, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={{ ...fadeIn, visible: { ...fadeIn.visible, transition: { ...fadeIn.visible.transition, delay: i * 0.1 } } }}
              className="text-center flex flex-col items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: bg }}>
                <Icon size={22} strokeWidth={1.8} style={{ color: '#fff' }} />
              </div>
              <h4 className="font-sans text-sm font-bold mb-1" style={{ color: '#000' }}>{title}</h4>
              <p className="font-sans text-xs" style={{ color: '#888' }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
